import { Link } from "react-router-dom";
import { Button } from "@jambushrushti/components/ui/button";
import {
  ArrowRight,
  Mic,
  FileText,
  BookOpen,
  Loader2,
  Lock,
  Mail,
  Send,
} from "lucide-react";
import { useGuruvaniFromDB } from "@jambushrushti/hooks/useContent";
import { Input } from "@jambushrushti/components/ui/input";

import guruvaniImg1 from "@jambushrushti/assets/books/guruvani-1.jpg";
import guruvaniImg2 from "@jambushrushti/assets/books/guruvani-2.jpg";
import guruvaniImg3 from "@jambushrushti/assets/books/guruvani-3.jpg";

const typeIcons: Record<string, typeof Mic> = {
  Discourse: Mic,
  "Written Work": FileText,
  Letter: BookOpen,
  Notes: FileText,
};

const fallbackImages = [guruvaniImg1, guruvaniImg2, guruvaniImg3];

export function GuruvaniPreview() {
  const { data: guruvaniItems = [], isLoading } = useGuruvaniFromDB();

  const displayItems = guruvaniItems.slice(0, 3);

  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <p
            className="mb-3 text-xs font-semibold uppercase tracking-wider"
            style={{ fontFamily: "Inter, sans-serif", color: "#C9A227" }}
          >
            Sacred Teachings
          </p>

          <h2
            className="mb-4 text-3xl font-bold md:text-4xl"
            style={{ fontFamily: "Playfair Display, serif", color: "#2D2A26" }}
          >
            Guruvani
          </h2>

          <p
            className="text-[15px] leading-relaxed"
            style={{ fontFamily: "Inter, sans-serif", color: "#5A5650" }}
          >
            Explore the profound discourses, teachings, and spiritual guidance
            from Gurudev Muni Jambuvijayaji Maharaj's lifetime of devotion.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#2D2A26]" />
          </div>
        ) : (
          <>
            {/* Guruvani Cards */}
            <div className="mx-auto mb-10 grid max-w-5xl gap-5 md:grid-cols-3">
              {displayItems.map((item, index) => {
                const Icon = typeIcons[item.category || ""] || FileText;
                return (
                  <Link
                    key={item.id}
                    to={item.is_restricted ? "/auth" : `/guruvani/${item.id}`}
                    className="group overflow-hidden rounded-xl border border-[#E5E0D5] bg-[#FAF7F2] transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={
                          item.image_url ||
                          fallbackImages[index % fallbackImages.length]
                        }
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {item.is_restricted && (
                        <div className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/90">
                          <Lock className="h-3.5 w-3.5 text-[#5A5650]" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="mb-2.5 flex items-center gap-2">
                        <span
                          className="rounded-full bg-[#C9A227]/10 px-2.5 py-1 text-xs font-medium text-[#C9A227]"
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          {item.category || "Teaching"}
                        </span>
                      </div>
                      <h3
                        className="mb-2 line-clamp-2 text-base font-semibold transition-opacity group-hover:opacity-80"
                        style={{
                          fontFamily: "Playfair Display, serif",
                          color: "#2D2A26",
                        }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="line-clamp-2 text-sm leading-relaxed"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          color: "#5A5650",
                        }}
                      >
                        {item.content ||
                          "Explore this sacred teaching from Gurudev's collection of wisdom and spiritual guidance."}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>

            {displayItems.length === 0 && (
              <div className="py-10 text-center">
                <p
                  style={{ fontFamily: "Inter, sans-serif", color: "#5A5650" }}
                >
                  No Guruvani items available yet.
                </p>
              </div>
            )}
          </>
        )}

        {/* Newsletter Signup */}
        <div className="mx-auto mb-8 max-w-2xl">
          <div className="flex flex-col items-center gap-4 rounded-xl border border-[#E5E0D5] bg-[#FAF7F2] p-5 sm:flex-row">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#C9A227]/10">
              <Mail className="h-5 w-5 text-[#C9A227]" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p
                className="mb-0.5 text-sm font-semibold"
                style={{ fontFamily: "Inter, sans-serif", color: "#2D2A26" }}
              >
                Subscribe to receive updates
              </p>
              <p
                className="text-xs"
                style={{ fontFamily: "Inter, sans-serif", color: "#5A5650" }}
              >
                Get notified when new teachings are published
              </p>
            </div>
            <div className="flex w-full gap-2 sm:w-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="h-9 min-w-[180px] text-sm"
                style={{ fontFamily: "Inter, sans-serif" }}
              />
              <Button className="h-9 bg-[#D4A03C] px-4 text-white hover:bg-[#C49030]">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            className="h-10 border-[#E5E0D5] bg-white px-6 text-sm text-[#2D2A26] hover:bg-gray-50"
            style={{ fontFamily: "Inter, sans-serif" }}
            asChild
          >
            <Link to="/guruvani">
              Explore All Teachings
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
